"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Check } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

export default function NewPlanTab() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    price: "",
    returnRate: "",
    duration: "",
    focus: "",
    description: "",
    performance: "",
    coverImage: null,
    coverImagePreview: "",
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Check auth and role
  useEffect(() => {
    if (!user || !token) {
      setApiError("Please log in to create plans");
      navigate("/login");
      return;
    }
    if (user.role !== "Admin" && user.role!=="Super Admin") {
      setApiError("You must be an admin to create plans");
      navigate("/");
      return;
    }
  }, [user, token, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setApiError(null);
  };

  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData({
        ...formData,
        coverImage: file,
        coverImagePreview: URL.createObjectURL(file),
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowConfirmation(true);
  };

  const confirmSubmission = async () => {
    setShowConfirmation(false);
    setApiError(null);
    const data = new FormData();
    data.append("name", formData.name);
    data.append("location", formData.location);
    data.append("price", formData.price);
    data.append("returnRate", formData.returnRate);
    data.append("duration", formData.duration);
    data.append("focus", formData.focus);
    data.append("description", formData.description);
    data.append("performance", formData.performance);
    if (formData.coverImage) {
      data.append("image", formData.coverImage);
    }

    try {
      const response = await axios.post("https://backend.emerald.azadnoori.com/api/v1/plans", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess(true);
      setFormData({
        name: "",
        location: "",
        price: "",
        returnRate: "",
        duration: "",
        focus: "",
        description: "",
        performance: "",
        coverImage: null,
        coverImagePreview: "",
      });
      setTimeout(() => setSuccess(false), 3000);
      // Notify PlansManagementTab (if integrated with API)
      localStorage.setItem("planAdded", Date.now().toString());
      setTimeout(() => localStorage.removeItem("planAdded"), 1000);
    } catch (error) {
      let errorMessage = error.response?.data?.message || "Failed to create plan.";
      if (error.response?.status === 401) {
        errorMessage = "Session expired. Please log in again.";
        navigate("/login");
      }
      setApiError(errorMessage);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <motion.form
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        onSubmit={handleSubmit}
      >
        {success && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-emerald-100 border border-emerald-200 text-emerald-800 rounded-lg p-4 mb-6"
          >
            Plan created successfully!
          </motion.div>
        )}
        {apiError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-red-100 border border-red-200 text-red-800 rounded-lg p-4 mb-6"
          >
            {apiError}
          </motion.div>
        )}

        <div className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Plan Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={4}
              value={formData.description}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
              Price ($)
            </label>
            <input
              id="price"
              name="price"
              type="number"
              min="0"
              step="0.01"
              value={formData.price}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              id="location"
              name="location"
              type="text"
              value={formData.location}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="returnRate" className="block text-sm font-medium text-gray-700 mb-1">
              Return Rate (e.g. "30%")
            </label>
            <input
              id="returnRate"
              name="returnRate"
              type="text"
              value={formData.returnRate}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
             所需的
            />
          </div>

          <div>
            <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
              Duration (e.g. "1-Year investment cycle")
            </label>
            <input
              id="duration"
              name="duration"
              type="text"
              value={formData.duration}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="focus" className="block text-sm font-medium text-gray-700 mb-1">
              Focus
            </label>
            <textarea
              id="focus"
              name="focus"
              rows={2}
              value={formData.focus}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="performance" className="block text-sm font-medium text-gray-700 mb-1">
              Performance (e.g. "+30% Return")
            </label>
            <input
              id="performance"
              name="performance"
              type="text"
              value={formData.performance}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
             
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input type="file" id="coverImage" accept="image/*" onChange={handleImageUpload} className="hidden" />
              <label htmlFor="coverImage" className="flex flex-col items-center justify-center cursor-pointer">
                <Upload className="h-10 w-10 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">Click to upload cover image</span>
                <span className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 5MB</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Create Plan
            </button>
          </div>
        </div>
      </motion.form>

      {/* Live Preview */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="flex flex-col items-center justify-center"
      >
        <h3 className="text-lg font-medium text-gray-700 mb-4">Live Preview</h3>
        <motion.div layout className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="h-48 bg-gray-200 relative">
            {formData.coverImagePreview ? (
              <img
                src={formData.coverImagePreview || "/placeholder.svg"}
                alt="Cover"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">No image uploaded</div>
            )}
            {formData.performance && (
              <div className="absolute top-4 right-4 bg-green-500 text-white px-2 py-1 rounded-md text-sm font-medium">
                {formData.performance}
              </div>
            )}
          </div>
          <div className="p-4">
            <h3 className="text-xl font-bold text-gray-800 mb-2">{formData.name || "Plan Name"}</h3>
            <p className="text-gray-600 text-sm mb-4 line-clamp-3">
              {formData.description || "Plan description will appear here..."}
            </p>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-500 text-xs">Location</p>
                  <p className="font-medium">{formData.location || "Not specified"}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-500 text-xs">Price</p>
                  <p className="font-bold text-blue-600">{formData.price ? `$${formData.price}` : "$0.00"}</p>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-500 text-xs">Return Rate</p>
                  <p className="font-medium">{formData.returnRate || "Not specified"}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-500 text-xs">Duration</p>
                  <p className="font-medium">{formData.duration || "Not specified"}</p>
                </div>
              </div>

              {formData.focus && (
                <div>
                  <p className="text-gray-500 text-xs">Focus</p>
                  <p className="text-sm text-gray-700 line-clamp-2">{formData.focus}</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Confirmation Dialog */}
      <AnimatePresence>
        {showConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <h3 className="text-xl font-bold text-gray-800 mb-2">Confirm Submission</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to create this investment plan?</p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSubmission}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <Check size={18} className="mr-2" />
                  Confirm
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}